import styled from '@emotion/styled';

import {Loader} from 'Loader';

import theme from 'theme';
import {SizeTokenValue} from 'common';
import {CloseIcon} from 'icons';

import type {ButtonHTMLAttributes, CSSProperties} from 'react';
import type {InputInternalProps, InputStyles} from '../types';

type InputButtonStyleProps = Pick<InputStyles, 'isSmall' | 'isDisabled'>;

const inputButtonStyle = (props: InputButtonStyleProps, acc: CSSProperties | undefined): CSSProperties => {
  const size = props.isSmall ? 16 : 32;
  const color = theme.colors.grey[props.isDisabled ? 40 : 60];
  const pointerEvents = props.isDisabled ? 'none' : undefined;

  return {
    color,
    pointerEvents,
    width: size,
    height: size,

    ...acc,
  };
};

const InputButtonComponent = styled.button`
  flex-shrink: 0;
  padding: 0;
  margin-right: -4px;
  border: 0;
  outline: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  line-height: 0;

  :hover {
    background-color: ${theme.colors.grey[20]};
  }

  :active {
    background-color: ${theme.colors.grey[30]};
  }
`;

export const InputButton: React.FC<InputButtonStyleProps & ButtonHTMLAttributes<HTMLButtonElement>> = props => {
  const {isSmall, isDisabled, style, ...rest} = props;

  return <InputButtonComponent style={inputButtonStyle({isSmall, isDisabled}, style)} {...rest} />;
};

export const EndAdornment: React.FC<InputInternalProps> = props => {
  const {
    onClearClick,
    endAdornment = null,
    style: {isDisabled, isReadOnly, isSmall, isLoading, isFilled},
  } = props;

  const iconSize = isSmall ? SizeTokenValue.Small : SizeTokenValue.Medium;

  if (isLoading) {
    return (
      <>
        <Loader size={iconSize} />
        {endAdornment}
      </>
    );
  }

  if (!isDisabled && !isReadOnly && isFilled && onClearClick) {
    return (
      <>
        <InputButton type="button" onClick={onClearClick} isSmall={isSmall}>
          <CloseIcon size={iconSize} />
        </InputButton>
        {endAdornment}
      </>
    );
  }

  return endAdornment;
};
